import {
  BasisTheoryProvider,
  CardExpirationDateElement,
  CardNumberElement,
  CardVerificationCodeElement,
  ICardExpirationDateElement,
  ICardNumberElement,
  ICardVerificationCodeElement,
} from '@basis-theory/react-elements'
import { useFormikContext } from 'formik'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useConfirmDeposit } from '@pages/Traders-Room/Deposit/Amount/hooks/useConfirmDeposit'
import { BtField } from '@pages/Traders-Room/Deposit/CardDetails/BtField'
import { DepositFormFields } from '@pages/Traders-Room/Deposit/components/DepositFormFields'

import { Heading6 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { PopoverInput } from '@ui/components/Popover'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { CardIcon } from '@ui/icons/Card.icon'

import { mapCardTypeToBrand } from '@utils/cardBrand'

import { NameDto } from '@model/NameDto'

import { RawFormField } from '@global/formField/FormField'
import { detectThemeClientSide } from '@global/theme/Theme'

import { useFundingNav } from '@hooks/useFundingNav'

import { getCardByCardType, getCardIconByBtBrand } from '@assets/cards'

import { AnimatedSwitch } from '@shared/animations/AnimatedSwitch'
import { Box } from '@shared/components/Box'
import { FundingSummary } from '@shared/components/Funding/FundingSummary/FundingSummary'
import { Loading } from '@shared/components/Loading/Loading'
import { Notice } from '@shared/components/Notice'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import { DepositFormValues } from '../DepositFlowLayout/types'
import { getCardholderFieldName } from '../helpers'
import {
  EMPTY_BT_FIELD_STATE,
  formatExpiryDate,
  formatMaskedCardNumber,
  hasTokenizedCardParams,
  toBtFieldState,
  type BtFieldState,
} from './helpers'
import { useSavedCards } from './hooks/useSavedCards'
import {
  tokenizedCardAuthenticationField,
  tokenizedCardIdField,
  tokenizedCardIntentField,
  useTokenizedCard,
} from './hooks/useTokenizedCard'

type BtFocusedField = 'new-number' | 'new-expiry' | 'new-cvc' | 'saved-cvc' | null
type CardFlow = 'saved' | 'new'

export const DepositCardDetailsPage: React.FC = () => {
  const { t } = useTranslation()
  const { go, state } = useFundingNav()
  const { values, setFieldValue, validateForm, setSubmitting, isSubmitting } =
    useFormikContext<DepositFormValues>()
  const amountValue = Number(values?.amount || 0)

  const provider = values.paymentProvider
  const providerForm = provider?.parameters?.form ?? []
  const tokenizedEnabled = hasTokenizedCardParams(providerForm)
  const { handleConfirm, submitError } = useConfirmDeposit()

  useEffect(() => {
    if (!tokenizedEnabled || amountValue <= 0) {
      go('../amount', { replace: true })
    }
  }, [tokenizedEnabled, go, amountValue])

  const { savedCards, isLoading: isSavedCardsLoading } = useSavedCards(tokenizedEnabled)
  const { prepare, btClient } = useTokenizedCard()
  const isBtReady = !!btClient.bt && !btClient.error
  const isBtLoading = !btClient.bt && !btClient.error
  const theme = detectThemeClientSide()

  const nameOfAccountField = useMemo(() => getCardholderFieldName(provider), [provider])

  const [cardFlow, setCardFlow] = useState<CardFlow>('new')
  const [selectedCardTokenId, setSelectedCardTokenId] = useState<string | null>(null)
  const [savedCardsError, setSavedCardsError] = useState(false)
  const [cardErrorMessage, setCardErrorMessage] = useState<string | null>(null)
  const [btFocusedField, setBtFocusedField] = useState<BtFocusedField>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const [btNewNumber, setBtNewNumber] = useState<BtFieldState>(EMPTY_BT_FIELD_STATE)
  const [btNewExpiry, setBtNewExpiry] = useState<BtFieldState>(EMPTY_BT_FIELD_STATE)
  const [btNewCvc, setBtNewCvc] = useState<BtFieldState>(EMPTY_BT_FIELD_STATE)
  const [btSavedCvc, setBtSavedCvc] = useState<BtFieldState>(EMPTY_BT_FIELD_STATE)

  const cardNumberRef = useRef<ICardNumberElement | null>(null)
  const cardExpiryRef = useRef<ICardExpirationDateElement | null>(null)
  const newCardCvcRef = useRef<ICardVerificationCodeElement | null>(null)
  const savedCardCvcRef = useRef<ICardVerificationCodeElement | null>(null)

  const cardFlowIndex = cardFlow === 'saved' ? 0 : 1
  const prevCardFlowIndex = useRef(cardFlowIndex)

  useEffect(() => {
    prevCardFlowIndex.current = cardFlowIndex
  }, [cardFlowIndex])

  const showSavedCardsToggle = tokenizedEnabled && (savedCards?.length ?? 0) > 0

  useEffect(() => {
    if (!tokenizedEnabled) return
    if (!savedCards) return

    if (savedCards.length === 0) {
      setCardFlow('new')
      setSelectedCardTokenId(null)
      return
    }

    setCardFlow('saved')
    setSelectedCardTokenId((prev) => prev ?? savedCards[0]?.cardTokenId ?? null)
  }, [tokenizedEnabled, savedCards])

  const selectedSavedCard = useMemo(
    () => savedCards?.find((c) => c.cardTokenId === selectedCardTokenId) ?? null,
    [savedCards, selectedCardTokenId]
  )

  const savedBrand = mapCardTypeToBrand(selectedSavedCard?.cardType)

  const savedCardOptions = useMemo(() => {
    if (!savedCards?.length) return []
    return savedCards.map((card) => ({
      id: card.cardTokenId,
      name: formatMaskedCardNumber(card.cardNumber),
      trailText: formatExpiryDate(card.expirationDate),
      startIcon: getCardByCardType(card.cardType.id),
    }))
  }, [savedCards])

  useEffect(() => {
    if (cardFlow === 'saved' && selectedSavedCard?.cardHolderName) {
      setFieldValue(`fields.${nameOfAccountField}`, selectedSavedCard.cardHolderName)
    }
  }, [cardFlow, selectedSavedCard, nameOfAccountField, setFieldValue])

  useEffect(() => {
    setCardErrorMessage(null)
    setBtFocusedField(null)

    if (cardFlow === 'saved') {
      setBtSavedCvc(EMPTY_BT_FIELD_STATE)
    } else {
      setBtNewNumber(EMPTY_BT_FIELD_STATE)
      setBtNewExpiry(EMPTY_BT_FIELD_STATE)
      setBtNewCvc(EMPTY_BT_FIELD_STATE)
    }
  }, [cardFlow, selectedCardTokenId, provider?.id])

  const isBtComplete =
    cardFlow === 'saved'
      ? btSavedCvc.complete && btSavedCvc.valid && !btSavedCvc.hasErrors
      : btNewNumber.complete &&
        btNewExpiry.complete &&
        btNewCvc.complete &&
        btNewNumber.valid &&
        btNewExpiry.valid &&
        btNewCvc.valid &&
        !btNewNumber.hasErrors &&
        !btNewExpiry.hasErrors &&
        !btNewCvc.hasErrors

  const hasCardholderName = Boolean(String(values.fields?.[nameOfAccountField] ?? '').trim())

  const canProceed =
    tokenizedEnabled &&
    isBtReady &&
    isBtComplete &&
    (cardFlow === 'saved' ? !!selectedCardTokenId : hasCardholderName)

  const handleSubmitDeposit = async () => {
    setCardErrorMessage(null)
    setSavedCardsError(false)

    const errors = await validateForm()
    if (Object.keys(errors).length > 0) return

    if (!isBtReady) {
      setCardErrorMessage(t('Card payment is temporarily unavailable.'))
      return
    }

    if (!canProceed) {
      setCardErrorMessage(t('Please enter your card details to continue.'))
      return
    }

    setSubmitting(true)
    const res = await prepare({
      flow: cardFlow,
      selectedSavedCardTokenId: selectedCardTokenId,
      savedCardCvcEl: savedCardCvcRef.current,
      newCard: {
        numberEl: cardNumberRef.current,
        expEl: cardExpiryRef.current,
        cvcEl: newCardCvcRef.current,
      },
    })

    if ('error' in res) {
      const msg =
        res.error === 'missing_bt_key'
          ? t('Card payment is temporarily unavailable.')
          : t('Something went wrong! Please try again later')
      setCardErrorMessage(msg)
      return
    }

    await setFieldValue(`fields.${tokenizedCardIdField}`, res.tokenizedCardId)
    await setFieldValue(`fields.${tokenizedCardIntentField}`, res.isIntent ? 'true' : 'false')
    await setFieldValue(`fields.${tokenizedCardAuthenticationField}`, res.authentication ?? '')

    await handleConfirm(res)
  }

  if (!tokenizedEnabled) return null

  const error = submitError ?? cardErrorMessage

  return (
    <FundingTemplate contentRef={contentRef} onBack={() => go(-1)} maxWidth={500}>
      <Box flex align='center' direction='column' gap='x32' fullWidth style={{ minHeight: '100%' }}>
        {error && <Notice id='deposit-preview-submit-error' type='danger' message={error} />}
        {savedCardsError && (
          <Notice
            id='saved-cards-error'
            type='danger'
            message={t('Something went wrong! Please try again later')}
          />
        )}
        <Heading6 medium>{t('Card Details')}</Heading6>

        <Loading showLoadingIcon isLoading={isSavedCardsLoading || isBtLoading}>
          {!isBtReady ? (
            <Notice
              id='card-payment-unavailable'
              type='danger'
              message={t('Card payment is temporarily unavailable.')}
            />
          ) : (
            <BasisTheoryProvider bt={btClient.bt} key={`bt-client-${String(theme)}`}>
              <Box flex direction='column' gap='x12' fullWidth>
                {showSavedCardsToggle && (
                  <SegmentedControl
                    size='md'
                    variant='default'
                    activeTab={cardFlow}
                    tabs={[
                      {
                        id: 'saved',
                        name: (
                          <>
                            {t('My Cards')}
                            {savedCards?.length ? (
                              <Badge size='xs' type='primary' contrast='high'>
                                {savedCards.length}
                              </Badge>
                            ) : null}
                          </>
                        ),
                        onClick: () => setCardFlow('saved'),
                      },
                      {
                        id: 'new',
                        name: t('New Card'),
                        onClick: () => {
                          setCardFlow('new')
                          setFieldValue(`fields.${nameOfAccountField}`, '')
                        },
                      },
                    ]}
                  />
                )}

                <AnimatedSwitch variant='morph' activeKey={cardFlow} activeIndex={cardFlowIndex}>
                  <Box flex direction='column' gap='x12' fullWidth>
                    {cardFlow === 'saved' ? (
                      <>
                        {showSavedCardsToggle && (
                          <PopoverInput
                            label={t('Card')}
                            placeholder={t('Select Card')}
                            value={selectedCardTokenId ?? ''}
                            options={savedCardOptions}
                            startIcon={getCardByCardType(selectedSavedCard?.cardType.id)}
                            rightText={formatExpiryDate(selectedSavedCard?.expirationDate)}
                            isFullWidth
                            selectionVariant='radio'
                            hasCheckMark={false}
                            dropdownListVariant='compact'
                            onChange={(option: NameDto<string | number>) => {
                              setSelectedCardTokenId(String(option.id))
                              savedCardCvcRef.current?.clear()
                            }}
                          />
                        )}

                        <SavedCardCvc
                          bt={btClient.bt}
                          savedBrand={savedBrand}
                          btFocusedField={btFocusedField}
                          setBtFocusedField={setBtFocusedField}
                          btSavedCvc={btSavedCvc}
                          setBtSavedCvc={setBtSavedCvc}
                          savedCardCvcRef={savedCardCvcRef}
                        />
                      </>
                    ) : (
                      <NewCardFields
                        bt={btClient.bt}
                        btFocusedField={btFocusedField}
                        setBtFocusedField={setBtFocusedField}
                        btNewNumber={btNewNumber}
                        btNewExpiry={btNewExpiry}
                        btNewCvc={btNewCvc}
                        setBtNewNumber={setBtNewNumber}
                        setBtNewExpiry={setBtNewExpiry}
                        setBtNewCvc={setBtNewCvc}
                        cardNumberRef={cardNumberRef}
                        cardExpiryRef={cardExpiryRef}
                        newCardCvcRef={newCardCvcRef}
                        nameOfAccountField={nameOfAccountField}
                      />
                    )}
                  </Box>
                </AnimatedSwitch>
              </Box>
            </BasisTheoryProvider>
          )}
        </Loading>

        <FundingSummary
          showAmount
          paymentProvider={values.paymentProvider}
          wallet={values.wallet}
          tradingAccount={values.tradingAccount}
          amount={values.amount}
          walletPaymentProviderLogo={state?.walletPaymentProvider?.logo}
        />

        <FullScreenFooter contentRef={contentRef} fullWidth>
          {cardFlow === 'new' && (
            <Notice
              noIcon
              id='card-ownership-confirmation-notice'
              type='info'
              message={t('Card ownership confirmation notice')}
            />
          )}
          <Box fullWidth maxWidth={{ desktop: 320 }} alignSelf='center'>
            <Button.Primary
              size='lg'
              type='button'
              fullWidth
              disabled={!canProceed}
              loading={isSubmitting}
              onClick={handleSubmitDeposit}
            >
              {t('Confirm')}
            </Button.Primary>
          </Box>
        </FullScreenFooter>
      </Box>
    </FundingTemplate>
  )
}

const SavedCardCvc: React.FC<{
  bt: any
  savedBrand: string | undefined
  btFocusedField: BtFocusedField
  setBtFocusedField: React.Dispatch<React.SetStateAction<BtFocusedField>>
  btSavedCvc: BtFieldState
  setBtSavedCvc: React.Dispatch<React.SetStateAction<BtFieldState>>
  savedCardCvcRef: React.RefObject<ICardVerificationCodeElement | null>
}> = ({
  bt,
  savedBrand,
  btFocusedField,
  setBtFocusedField,
  btSavedCvc,
  setBtSavedCvc,
  savedCardCvcRef,
}) => {
  return (
    <BtField
      hasValue={!btSavedCvc.empty}
      isFocused={btFocusedField === 'saved-cvc'}
      label='CVC/CVV'
      onClick={() => savedCardCvcRef.current?.focus()}
    >
      <CardVerificationCodeElement
        id='bt-saved-card-cvc'
        ref={savedCardCvcRef}
        bt={bt}
        inputMode='numeric'
        cardBrand={savedBrand}
        onFocus={() => setBtFocusedField('saved-cvc')}
        onBlur={() => setBtFocusedField((p) => (p === 'saved-cvc' ? null : p))}
        onChange={(e) => setBtSavedCvc(toBtFieldState(e))}
      />
    </BtField>
  )
}

const NewCardFields: React.FC<{
  bt: any
  btFocusedField: BtFocusedField
  setBtFocusedField: React.Dispatch<React.SetStateAction<BtFocusedField>>
  btNewNumber: BtFieldState
  btNewExpiry: BtFieldState
  btNewCvc: BtFieldState
  setBtNewNumber: React.Dispatch<React.SetStateAction<BtFieldState>>
  setBtNewExpiry: React.Dispatch<React.SetStateAction<BtFieldState>>
  setBtNewCvc: React.Dispatch<React.SetStateAction<BtFieldState>>
  cardNumberRef: React.RefObject<ICardNumberElement | null>
  cardExpiryRef: React.RefObject<ICardExpirationDateElement | null>
  newCardCvcRef: React.RefObject<ICardVerificationCodeElement | null>
  nameOfAccountField: string
}> = ({
  bt,
  btFocusedField,
  setBtFocusedField,
  btNewNumber,
  btNewExpiry,
  btNewCvc,
  setBtNewNumber,
  setBtNewExpiry,
  setBtNewCvc,
  cardNumberRef,
  cardExpiryRef,
  newCardCvcRef,
  nameOfAccountField,
}) => {
  const { t } = useTranslation()
  const numberBrandOrNetwork = btNewNumber.selectedNetwork ?? btNewNumber.cardBrand

  const numberIcon = useMemo(
    () => (numberBrandOrNetwork ? getCardIconByBtBrand(numberBrandOrNetwork) : CardIcon),
    [numberBrandOrNetwork]
  )

  return (
    <>
      <DepositFormFields />

      <RawFormField name={`fields.${nameOfAccountField}`} label={t('Cardholder Name')} required />

      <BtField
        hasValue={!btNewNumber.empty}
        isFocused={btFocusedField === 'new-number'}
        label='Card number'
        rightIcon={numberIcon}
        onClick={() => cardNumberRef.current?.focus()}
      >
        <CardNumberElement
          id='bt-card-number'
          iconPosition='none'
          ref={cardNumberRef}
          bt={bt}
          inputMode='numeric'
          onFocus={() => setBtFocusedField('new-number')}
          onBlur={() => setBtFocusedField((p) => (p === 'new-number' ? null : p))}
          onChange={(e) => setBtNewNumber(toBtFieldState(e))}
        />
      </BtField>

      <Box flex gap='x12'>
        <BtField
          hasValue={!btNewExpiry.empty}
          isFocused={btFocusedField === 'new-expiry'}
          label='Expiry date'
          placeholder='MM/YY'
          onClick={() => cardExpiryRef.current?.focus()}
        >
          <CardExpirationDateElement
            id='bt-card-expiry'
            ref={cardExpiryRef}
            bt={bt}
            inputMode='numeric'
            onFocus={() => setBtFocusedField('new-expiry')}
            onBlur={() => setBtFocusedField((p) => (p === 'new-expiry' ? null : p))}
            onChange={(e) => setBtNewExpiry(toBtFieldState(e))}
          />
        </BtField>

        <BtField
          hasValue={!btNewCvc.empty}
          isFocused={btFocusedField === 'new-cvc'}
          label='CVC/CVV'
          onClick={() => newCardCvcRef.current?.focus()}
        >
          <CardVerificationCodeElement
            id='bt-card-cvc'
            ref={newCardCvcRef}
            bt={bt}
            inputMode='numeric'
            cardBrand={btNewNumber.cardBrand}
            onFocus={() => setBtFocusedField('new-cvc')}
            onBlur={() => setBtFocusedField((p) => (p === 'new-cvc' ? null : p))}
            onChange={(e) => setBtNewCvc(toBtFieldState(e))}
          />
        </BtField>
      </Box>
    </>
  )
}
